import { APP_MODULES_CONFIG } from "utils/features";
import { apiFetch } from "./apiFetch";

export const DEMO_MODE_ACTIVE = APP_MODULES_CONFIG.modeDemo;
export const OFFLINE_MODE_ACTIVE = DEMO_MODE_ACTIVE && process.env.REACT_APP_MODE_OFFLINE === "true";

// ─── Auth ───────────────────────────────────────────────────────────────────

/**
 * Headers for authenticated API requests (JSON + Bearer token).
 * @param {string|null} tokenOverride - Optional token; otherwise uses localStorage "token".
 */
export const getAuthHeaders = (tokenOverride = null) => {
  const token = tokenOverride ?? localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// ─── Stream progress (fetch body read) ───────────────────────────────────────

/**
 * Read a fetch response body with progress based on Content-Length.
 * @param {ReadableStreamDefaultReader} reader
 * @param {number} contentLength
 * @param {Function} onProgress - (percent: number) => void
 * @returns {Promise<string>} Decoded response text.
 */
export const getRequestProgress = async (
  reader,
  contentLength,
  onProgress = () => {}
) => {
  try {
    let receivedLength = 0;
    const chunks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      const percent = contentLength
        ? Math.min((receivedLength / contentLength) * 100, 100)
        : 0;
      onProgress(percent);
    }

    const combined = new Uint8Array(receivedLength);
    let position = 0;
    for (const chunk of chunks) {
      combined.set(chunk, position);
      position += chunk.length;
    }

    return new TextDecoder("utf-8").decode(combined);
  } catch (error) {
    throw error;
  }
};

/* ================================ PRESIGNED URL VALIDATION ================================ */
/**
 * Validates a presigned S3 url before exposing it
 * All network failures and CORS blocks are caught silently and the URL is passed through
 * unchanged when the check can't be completed, this ensures the flow doesn't break regardless of S3 CORS configuration
 *
 * Returns false only when the server responds with a confirmed non-2xx status (response.ok === false)
 *
 * @param {string} url - Presigned URL to validate
 * @param {AbortSignal} [externalSignal] - Optional, external abort signal controller
 * @returns {Promise<boolean>} false if the URL is confirmed faulty, true otherwise
 */
export async function isPresignedUrlReachable(url, externalSignal) {
  const abortController = new AbortController();
  const timer = setTimeout(() => abortController.abort(), 4000);

  let externalListener;
  if (externalSignal) {
    externalListener = () => abortController.abort();
    externalSignal.addEventListener("abort", externalListener);
  }

  try {
    // Presigned URLs are signed for GET access
    const response = await fetch(url, { method: "GET", signal: abortController.signal });

    // Aborting immediately after headers arrive cancels the body download, only the headers are needed to validate the URL
    abortController.abort();

    if (!response.ok) return false; // S3 error
    return true; // Everything else
  } catch {
    // CORS block, network failure, timeout, or external abort — treat as valid
    return true;
  } finally {
    clearTimeout(timer);
    if (externalSignal && externalListener) {
      externalSignal.removeEventListener("abort", externalListener);
    }
  }
};

// ─── Bulk progress simulator ────────────────────────────────────────────────

/**
 * Estimate a simulated duration (ms) for a bulk operation based on item count
 * Clamped to the 2–10 second range to keep the progress bar feeling
 * responsive without jumping to completion instantly
 *
 * @param {number} itemCount - Number of items being processed.
 * @returns {number} Estimated duration in milliseconds.
 */
const estimateBulkDuration = (itemCount) => Math.min(Math.max((itemCount || 1) * 100, 2000), 10000);

/**
 * Start a simulated progress timer that eases towards 95%
 *
 * @param {number} estimatedDuration - Total estimated duration in ms
 * @param {Function} onProgress - Callback receiving a 0–95 integer
 * @returns {{ clear: () => void }} Object with a clear() method to stop the timer
 */
const startSimulatedProgress = (estimatedDuration, onProgress) => {
  const startTime = Date.now();
  let progressInterval;

  const updateProgress = () => {
    const elapsed = Date.now() - startTime;
    const ratio = Math.min(elapsed / estimatedDuration, 1);
    const easedProgress = 1 - Math.pow(1 - ratio, 3);
    const progress = Math.floor(easedProgress * 95);
    onProgress(progress);
    if (ratio >= 1) clearInterval(progressInterval);
  };

  progressInterval = setInterval(updateProgress, 100);

  return { clear: () => clearInterval(progressInterval) };
};

/**
 * Shared bulk-operation simulator
 *
 * Runs a simulated progress bar while the real request is in running in the background and jumps to 100% on completion
 * This is a temporary solution until websocket/SSE is implemented for these operations
 *
 * @param {Object} params
 * @param {string} params.endpoint - API URL path
 * @param {Object} params.fetchOptions - Fetch options to send to apiFetch (method, headers, body, signal, …).
 * @param {number} [params.itemCount=1] - Number of items being processed
 * @param {number} [params.estimatedDuration] - Explicit duration in ms
 * @param {Function} [params.onProgress] - Progress callback receiving a 0–100 integer
 * @param {string} [params.errorLabel] - Label used as an error log on failure
 * @returns {Promise<any>} Parsed JSON response body
 */
export const runBulkWithSimulatedProgress = async ({
  endpoint,
  fetchOptions,
  itemCount = 1,
  estimatedDuration,
  onProgress = () => {},
  errorLabel = "Bulk operation error",
}) => {
  const duration = estimatedDuration ?? estimateBulkDuration(itemCount);

  try {
    const progress = startSimulatedProgress(duration, onProgress);

    const response = await apiFetch(endpoint, fetchOptions);

    progress.clear();
    onProgress(100);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message || `Request failed with status ${response.status}`,
      );
      error.detail = errorData.detail;
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error(`${errorLabel}:`, error);
    throw error;
  }
};